import { useContext, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { toast } from "react-toastify";
import { PaymentContext } from "../../contexts/PaymentContext";
import { TOASTPROPS } from "../../shared/Constants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { cn } from "../../lib/utils";
import PaymentMethod from "./PaymentMethod";
import Checkout from "./Checkout";
import SavedCardPayment from "./SavedCardPayment";
import PixCheckout from "./PixCheckout";
import PixRecurringSetup from "./PixRecurringSetup";
import InvoiceConfigModal from "./InvoiceConfigModal";

const paymentStageTitle: { [key: string]: string } = {
  methods: "Métodos de pagamento",
  "methods-card": "Métodos de pagamento",
  "add-card": "Adicionar cartão",
  payment: "Pagamento",
  "payment-saved-card": "Pagamento",
  "payment-pix": "Pagamento PIX",
  "pix-recurring-subscription": "Assinatura via Pix Automático",
  "invoice-config": "Dados Fiscais",
};

const previousPaymentStage: { [key: string]: string } = {
  "methods-card": "methods",
  "add-card": "methods",
  payment: "methods",
  "payment-saved-card": "methods",
  "payment-pix": "methods",
  "pix-recurring-subscription": "methods",
};

export default function PaymentModal() {
  const { paymentStage, setPaymentStage, paymentToast, setPaymentToast } =
    useContext(PaymentContext);

  useEffect(() => {
    if (paymentToast) {
      switch (paymentToast.type) {
        case "error":
          toast.error(paymentToast.message, TOASTPROPS);
          break;
        case "success":
          toast.success(paymentToast.message, TOASTPROPS);
          break;
        case "info":
          toast.info(paymentToast.message, TOASTPROPS);
          break;
      }
    }
  }, [paymentToast]);

  const handleClose = () => {
    setPaymentToast({});
    setPaymentStage("refresh");
  };

  const handleReturn = () => {
    setPaymentStage(previousPaymentStage[paymentStage]);
  };

  const checkReturnButton = () => {
    return previousPaymentStage.hasOwnProperty(paymentStage);
  };

  const isStripePayment =
    paymentStage === "payment" ||
    paymentStage === "add-card" ||
    paymentStage === "payment-pix" ||
    paymentStage.startsWith("pix-recurring");

  const open = paymentStage !== "" && paymentStage !== "refresh";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className={cn(
          "gap-0 p-0",
          isStripePayment ? "max-w-[500px]" : "max-w-[800px]"
        )}
      >
        <DialogHeader className="flex-row items-center justify-between space-y-0 border-b border-border px-6 py-5 pr-14">
          <div className="flex items-center gap-2">
            {checkReturnButton() && (
              <button
                onClick={handleReturn}
                title="Voltar"
                aria-label="Voltar"
                className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
              </button>
            )}
            <DialogTitle>
              {paymentStageTitle[paymentStage]}
            </DialogTitle>
          </div>
        </DialogHeader>
        <div className="min-h-0">
          {paymentStage === "methods" && <PaymentMethod />}
          {paymentStage === "methods-card" && <PaymentMethod showCardOptionsByDefault />}
          {paymentStage === "add-card" && <Checkout setup={true} />}
          {paymentStage === "payment" && <Checkout setup={false} />}
          {paymentStage === "payment-saved-card" && <SavedCardPayment />}
          {paymentStage === "payment-pix" && <PixCheckout />}
          {paymentStage === "pix-recurring-subscription" && (
            <PixRecurringSetup purpose="subscription" />
          )}
          {paymentStage === "invoice-config" && <InvoiceConfigModal />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
